// Global Instructions Rule Applied!

import { useCallback, useEffect, useState } from 'react'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { fetchFundStatementSettings } from '../services/fundsService.ts'
import { fetchFundGroupStatementSettings } from '../../fund_groups/services/fundGroupsService.ts'
import { FundStatementSettings } from '../../../state_manager/data_models/fund_statement_settings.ts'

/**
 * useFundStatementSettings — the investor statement settings of a fund, or
 * of a consolidated fund when `fundGroupId` is given (layout, partners'
 * capital page, entity statements, wording overrides), with a refetch run
 * after every field patch: the layout that applies and the text each wording
 * field shows are resolved server-side.
 *
 * Section-local detail, like the notice settings: not in activeRecords.
 */
export function useFundStatementSettings(fundId: string | null | undefined, fundGroupId?: string | null) {
  const fundIdString = fundId ? String(fundId) : null
  const groupIdString = fundGroupId ? String(fundGroupId) : null

  const [settings, setSettings] = useState<FundStatementSettings | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!fundIdString && !groupIdString) {
      setSettings(null)
      setLoading(false)
      setError(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const accessToken = getAccessToken(store.getState().auth)
      const res = groupIdString
        ? await fetchFundGroupStatementSettings(groupIdString, accessToken)
        : await fetchFundStatementSettings(fundIdString as string, accessToken)
      if (res.error) setError(res.error)
      if (res.data) setSettings(res.data)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load statement settings')
    } finally {
      setLoading(false)
    }
  }, [fundIdString, groupIdString])

  useEffect(() => {
    load()
  }, [load])

  return { settings, loading, error, refetch: load }
}
